import { useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { useVideo } from '../../../../context/videoContext';
import { useAttachTrack } from '../../../../helpers/video/useParticipantTracks';
import { calibrationConstants } from '../../../../helpers/calibration-constants';
import { describeMediaError } from '../../../../helpers/video/media';
import { Tile, LocalTileStatus, LocalMediaControls, WarningBanner } from './VideoTileStatus';

const StyledVideo = styled.video`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

// The local camera. Rendered from the local track directly (not the room's publication), so
// the preview appears as soon as the camera starts, before/without a Twilio connection.
// The <video> keeps id=<localParticipant.sid> and is registered in the track's attachments,
// which corners-fixation and useFaceApi rely on.
const LocalVideo = ({ parentVideoRef, disableBorderRadius, controls = false }) => {
  const video = useVideo();
  const { room, localTracks, mediaStatus, mediaError, permissionState, audioError, publishError, isCameraEnabled, isMicEnabled, retry } = video;
  const videoTrack = localTracks?.video || null;
  const videoRef = useRef(null);

  useAttachTrack(videoTrack, videoRef);

  useEffect(() => {
    if (parentVideoRef && videoRef.current && parentVideoRef.current !== videoRef.current) {
      parentVideoRef.current = videoRef.current;
    }
  });

  const status = (
    <LocalTileStatus
      mediaStatus={mediaStatus}
      mediaError={mediaError}
      permissionState={permissionState}
      hasVideo={Boolean(videoTrack)}
      isCameraEnabled={isCameraEnabled}
      onRetry={retry}
    />
  );

  let warning = null;
  if (publishError && room) {
    warning = (
      <WarningBanner actionLabel='Retry' onAction={retry}>
        Your video is not reaching the other participant: {publishError.message}
      </WarningBanner>
    );
  } else if (audioError && !controls && videoTrack) {
    warning = <WarningBanner>Microphone unavailable: {describeMediaError(audioError, 'microphone')}</WarningBanner>;
  }

  return (
    <Tile disableBorderRadius={disableBorderRadius} radius={calibrationConstants.VIDEO_BORDER_RADIUS} data-testid='local-video-tile'>
      <StyledVideo ref={videoRef} id={room?.localParticipant?.sid} autoPlay playsInline muted style={{ visibility: videoTrack ? 'visible' : 'hidden' }} />
      {status}
      {warning}
      {controls && (
        <LocalMediaControls
          isMicEnabled={isMicEnabled}
          isCameraEnabled={isCameraEnabled}
          hasAudio={Boolean(localTracks?.audio)}
          hasVideo={Boolean(videoTrack)}
          toggleMic={video.toggleMic}
          toggleCamera={video.toggleCamera}
          devices={video.devices}
          selectedDevices={video.selectedDevices}
          switchDevice={video.switchDevice}
          deviceError={video.deviceError || (audioError && videoTrack ? audioError : null)}
        />
      )}
    </Tile>
  );
};

export default LocalVideo;
